import { config } from '../config';
import { tokenStorage } from '../auth/tokenStorage';

export type WsStatus = 'connecting' | 'connected' | 'reconnecting' | 'offline';

export interface CellsAddedEvent {
  type: 'cells.added';
  cells: string[];
}

interface CabinSocketHandlers {
  onCellsAdded: (cells: string[]) => void;
  onStatus: (status: WsStatus) => void;
  refreshAccessToken: () => Promise<string | null>;
}

const PING_INTERVAL_MS = 25_000;
const MIN_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
const AUTH_TIMEOUT_MS = 5_000;

export class CabinSocket {
  private ws: WebSocket | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private authTimeout: ReturnType<typeof setTimeout> | null = null;
  private backoff = MIN_BACKOFF_MS;
  private closed = false;
  private authedOnce = false;
  private handlers: CabinSocketHandlers;

  constructor(handlers: CabinSocketHandlers) {
    this.handlers = handlers;
  }

  start() {
    this.closed = false;
    this.connect();
  }

  stop() {
    this.closed = true;
    this.clearTimers();
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // ignore
      }
      this.ws = null;
    }
  }

  private clearTimers() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.authTimeout) {
      clearTimeout(this.authTimeout);
      this.authTimeout = null;
    }
  }

  private async connect() {
    if (this.closed) return;
    this.handlers.onStatus(this.authedOnce ? 'reconnecting' : 'connecting');

    let token = tokenStorage.getAccess();
    if (!token) {
      token = await this.handlers.refreshAccessToken();
    }
    if (!token || this.closed) {
      this.scheduleReconnect();
      return;
    }

    try {
      this.ws = new WebSocket(config.wsUrl);
    } catch {
      this.scheduleReconnect();
      return;
    }

    const ws = this.ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'auth', token }));
      this.authTimeout = setTimeout(() => {
        try {
          ws.close();
        } catch {
          // ignore
        }
      }, AUTH_TIMEOUT_MS);
    };

    ws.onmessage = (ev) => {
      let msg: { type: string; cells?: string[] };
      try {
        msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
      } catch {
        return;
      }

      if (msg.type === 'auth.ok') {
        if (this.authTimeout) {
          clearTimeout(this.authTimeout);
          this.authTimeout = null;
        }
        this.authedOnce = true;
        this.backoff = MIN_BACKOFF_MS;
        this.handlers.onStatus('connected');

        this.pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL_MS);
        return;
      }

      if (msg.type === 'cells.added' && Array.isArray(msg.cells)) {
        this.handlers.onCellsAdded(msg.cells);
      }
    };

    ws.onerror = () => {
      // onclose will fire after this
    };

    ws.onclose = (ev) => {
      this.clearTimers();
      this.ws = null;
      if (this.closed) return;

      // Code 4401 means auth failure — try refreshing the token before next attempt.
      if (ev.code === 4401) {
        // Drop access token; refresh on next connect
        // (refreshAccessToken is invoked at the top of connect())
        // No backoff change; retry quickly.
        void this.handlers.refreshAccessToken().then(() => {
          this.scheduleReconnect(MIN_BACKOFF_MS);
        });
        return;
      }

      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(delay?: number) {
    if (this.closed) return;
    const ms = delay ?? jitter(this.backoff);
    this.handlers.onStatus(this.authedOnce ? 'reconnecting' : 'offline');
    this.reconnectTimer = setTimeout(() => {
      void this.connect();
    }, ms);
    this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
  }
}

function jitter(ms: number): number {
  return ms + Math.floor(Math.random() * Math.min(ms, 1_000));
}
